/**
 * Push Notification Utilities for BETALAND.ID Partner PWA
 * Handles subscription, permission requests, and notification dispatch.
 */

// VAPID public key — replace with your actual VAPID public key from web-push library
// Generate with: npx web-push generate-vapid-keys
const VAPID_PUBLIC_KEY = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY || '';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Request notification permission from the user.
 * Returns 'granted' | 'denied' | 'default'
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) return 'denied';
  if (Notification.permission === 'granted') return 'granted';
  return await Notification.requestPermission();
}

/**
 * Subscribe to push notifications via the service worker.
 * Returns the PushSubscription or null if not supported / permission denied.
 */
export async function subscribeToPush(): Promise<PushSubscription | null> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return null;

  const permission = await requestNotificationPermission();
  if (permission !== 'granted') return null;

  try {
    const registration = await navigator.serviceWorker.ready;

    // Check for existing subscription
    const existing = await registration.pushManager.getSubscription();
    if (existing) return existing;

    // If no VAPID key configured, use a basic subscription without applicationServerKey
    // This allows local testing; production requires a real VAPID key
    const subscribeOptions: PushSubscriptionOptionsInit = {
      userVisibleOnly: true,
    };

    if (VAPID_PUBLIC_KEY) {
      subscribeOptions.applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
    }

    const subscription = await registration.pushManager.subscribe(subscribeOptions);
    return subscription;
  } catch (err) {
    console.warn('[PWA] Push subscription failed:', err);
    return null;
  }
}

/**
 * Unsubscribe from push notifications.
 */
export async function unsubscribeFromPush(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) return false;
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      return await subscription.unsubscribe();
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Get current push subscription status.
 */
export async function getPushSubscription(): Promise<PushSubscription | null> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return null;
  try {
    const registration = await navigator.serviceWorker.ready;
    return await registration.pushManager.getSubscription();
  } catch {
    return null;
  }
}

/**
 * Show a local notification immediately (no server needed).
 * Useful for in-app events like new lead added.
 */
export async function showLocalNotification(
  title: string,
  options: {
    body: string;
    type?: 'lead' | 'booking' | 'commission' | 'general';
    url?: string;
  }
): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  const permission = Notification.permission;
  if (permission !== 'granted') return;

  try {
    const registration = await navigator.serviceWorker.ready;
    await registration.showNotification(title, {
      body: options.body,
      icon: '/assets/images/app_logo.png',
      badge: '/assets/images/app_logo.png',
      tag: `betaland-local-${Date.now()}`,
      vibrate: [200, 100, 200],
      data: { url: options.url || '/partner/mobile/home', type: options.type || 'general' },
      actions: [
        { action: 'open', title: '👁 Lihat' },
        { action: 'dismiss', title: 'Tutup' },
      ],
    } as NotificationOptions);
  } catch (err) {
    console.warn('[PWA] Local notification failed:', err);
  }
}

/**
 * Check if the app is running as an installed PWA.
 */
export function isRunningAsPWA(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true
  );
}
